import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ActivityIndicator, Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Card from '../../../components/Card';
import { Controller, useForm } from 'react-hook-form';
import { WindowInformation } from '../../../services/window-information';
import { useQuery } from '@tanstack/react-query';
import { citizenService } from '../../../services/central-api/citizen';
import { useAuth } from '../../../context/Auth';
import { translate } from '../../../services/translate';
import { ScrollView } from 'react-native-web-hover';
import Header from '../../../components/Header';
import FormInput from '../../../components/FormInput';
import MyPhoneInput from '../../../components/MyPhoneInput';
import MyCpfInput from '../../../components/MyCpfInput';
import ProfilePicture from '../../../components/user/ProfilePicture';
import { applyMask } from '../../../components/helpers/documents';
import { validUfs } from '../../third-party-approved-companies/InscriptionsCommons';
import Badge, { BadgeColor } from '../../../components/Badge';
import MyAppText from '../../../components/MyAppText';
import DeactivateCitizenModal from './DeactivateCitizenModal';
import FormActions from '../../../components/FormActions';
import Toast from 'react-native-toast-message';

interface Props {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    citizenId: number;
}

const defaultEmptyValues: CitizenFormObject = {
    cep: '',
    address: '',
    number: '',
    city: '',
    neighborhood: '',
    state: '',
    document: '',
    facePictureImageUrl: '',
    name: '',
    phone: '',
    citizenBlockReason: undefined
};

export default function CitizenManagementModal({ isModalVisible, setModalVisible, citizenId }: Props) {
    const { user: authenticatedUser } = useAuth();
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const inViewMode = citizenId != undefined && citizenId > 0;

    const queryResult = useQuery({
        queryKey: ['citizenById', citizenId, isModalVisible],
        queryFn: () => citizenService.getCitizenById(citizenId!),
        enabled: !!authenticatedUser && citizenId > 0 && isModalVisible,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;
    const { data, refetch } = queryResult;
    const [showDeactivateCitizenModal, setShowDeactivateCitizenModal] = useState<boolean>(false);

    async function setForm(data: Citizen) {
        form.reset({
            cep: data.cep,
            address: data.address,
            number: data.number,
            city: data.city,
            neighborhood: data.neighborhood,
            state: data.state.toUpperCase(),
            document: data.document,
            name: data.name,
            phone: data.phone,
            facePictureImageUrl: data.facePictureImageUrl,
            citizenBlockReason: data.citizenBlockReason
        });
    }

    useEffect(() => {
        if (!authenticatedUser || !data) {
            return;
        }
        setForm(data);

    }, [data, authenticatedUser]);

    const form = useForm<CitizenFormObject>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const closeModal = () => {
        form.reset(defaultEmptyValues);
        setModalVisible(false);
    };

    function getCitizenStatus(deactivate: string | null, verified: boolean) {
        let customName = 'active';
        let badgeColor: BadgeColor = 'green';

        if (deactivate) {
            customName = 'disabled_alt';
            badgeColor = 'red';
        } else if (!verified) {
            customName = 'waiting';
            badgeColor = 'orange';
        }

        return <View style={[{ justifyContent: 'center' }]}>
            <Badge mode='SQUARE' size='medium' color={badgeColor}>{translate(customName)}</Badge>
        </View>;
    }

    async function toggleCitizenActivation(citizenId: number): Promise<boolean> {
        try {
            await citizenService.toggleCitizenActivation(citizenId);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            return true;
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return false;
        }
    }

    if (!data) {
        return <></>;
    }

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <Card>
                        {isLoading ?
                            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 250 }}>
                                <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                            </View>
                            : <>
                                <Header
                                    title={translate(inViewMode ? 'viewCitizen' : 'registerCitizen')}
                                    actions={data ? getCitizenStatus(data.deactivate, data.verified) : undefined}
                                />
                                <Card style={{ padding: 0, marginBottom: 12 }}>
                                    <ScrollView contentContainerStyle={{ flexDirection: 'column', flex: 1, gap: 24 }}>
                                        <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
                                            <Controller
                                                control={form.control}
                                                name='facePictureImageUrl'
                                                render={({ field }) => (
                                                    <ProfilePicture
                                                        imageStyle={{
                                                            height: 128,
                                                            width: 128,
                                                            margin: 0,
                                                            borderWidth: 1,
                                                            borderRadius: 99999,
                                                            borderColor: theme.colors.neutral['200']
                                                        }}
                                                        emptyStyle={{
                                                            margin: 0,
                                                            borderWidth: 1,
                                                            borderRadius: 99999,
                                                            borderColor: theme.colors.neutral['200']
                                                        }}
                                                        asAvatar
                                                        photo={field.value}
                                                        setProfilePicture={(val) => field.onChange(val)}
                                                        disabled={inViewMode}
                                                    />
                                                )}
                                            />
                                        </View>
                                        {form.watch('citizenBlockReason') && <View style={{ flexDirection: 'column' }}>
                                            <Header
                                                title={translate('blockReason')}
                                                titleStyle={{ fontSize: 16, color: theme.colors.primary['500'] }}
                                            />
                                            <MyAppText style={[styles.fieldsLabelStyleReason]}>{form.watch('citizenBlockReason.description')}</MyAppText>
                                        </View>}
                                        <View style={{ flexDirection: 'column' }}>
                                            <Header
                                                title={translate('citizenGeneralInfo')}
                                                titleStyle={{ fontSize: 16, color: theme.colors.primary['500'] }}
                                            />
                                            <View style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 16 }}>
                                                <FormInput
                                                    form={form}
                                                    fieldName='name'
                                                    label={translate('fullName')}
                                                    labelStyle={styles.fieldsLabelStyle}
                                                    viewStyle={styles.itemView(windowInfo.isMobile ? 1 : 2)}
                                                    disabled={inViewMode}
                                                />
                                                <MyCpfInput
                                                    form={form}
                                                    fieldName='document'
                                                    labelStyle={styles.fieldsLabelStyle}
                                                    viewStyle={styles.itemView(1)}
                                                    disabled={inViewMode}
                                                />
                                                <MyPhoneInput
                                                    form={form}
                                                    fieldName='phone'
                                                    placeholder='(00) 00000-0000'
                                                    label={translate('phone')}
                                                    labelStyle={{
                                                        ...styles.fieldsLabelStyle,
                                                        height: 'auto'
                                                    }}
                                                    viewStyle={styles.itemView(1)}
                                                    disabled={inViewMode}
                                                />

                                            </View>
                                        </View>
                                        <View style={{ flexDirection: 'column' }}>
                                            <Header
                                                title={translate('addressDataCitizen')}
                                                titleStyle={{ fontSize: 16, color: theme.colors.primary['500'] }}
                                            />
                                            <View style={{ flexDirection: 'column', gap: 16 }}>
                                                <View style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 16 }}>
                                                    <FormInput
                                                        form={form}
                                                        fieldName='cep'
                                                        label={translate('cep')}
                                                        maxLength={9}
                                                        mode='NUMBER'
                                                        mask={(val) => applyMask(val, 'cep')}
                                                        labelStyle={styles.fieldsLabelStyle}
                                                        viewStyle={styles.itemView(1)}
                                                        disabled={inViewMode}
                                                    />
                                                    <FormInput
                                                        form={form}
                                                        fieldName='address'
                                                        label={translate('street')}
                                                        labelStyle={styles.fieldsLabelStyle}
                                                        viewStyle={styles.itemView(windowInfo.isMobile ? 1 : 3)}
                                                        disabled={inViewMode}
                                                    />
                                                    <FormInput
                                                        form={form}
                                                        fieldName='number'
                                                        label={translate('number')}
                                                        mode='NUMBER'
                                                        labelStyle={styles.fieldsLabelStyle}
                                                        viewStyle={styles.itemView(1)}
                                                        disabled={inViewMode}
                                                    />
                                                </View>
                                                <View style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 16 }}>
                                                    <FormInput
                                                        form={form}
                                                        fieldName='city'
                                                        label={translate('city')}
                                                        labelStyle={styles.fieldsLabelStyle}
                                                        viewStyle={styles.itemView(windowInfo.isMobile ? 1 : 2)}
                                                        disabled={inViewMode}
                                                    />
                                                    <FormInput
                                                        form={form}
                                                        fieldName='neighborhood'
                                                        label={translate('neighborhood')}
                                                        labelStyle={styles.fieldsLabelStyle}
                                                        viewStyle={styles.itemView(windowInfo.isMobile ? 1 : 3)}
                                                        disabled={inViewMode}
                                                    />
                                                    <FormInput
                                                        form={form}
                                                        fieldName='state'
                                                        label={translate('uf')}
                                                        customValidations={{
                                                            validUf: (val: string) => validUfs.includes(val.toLowerCase()) || translate('invalid_uf')
                                                        }}
                                                        maxLength={2}
                                                        labelStyle={styles.fieldsLabelStyle}
                                                        viewStyle={styles.itemView(1)}
                                                        disabled={inViewMode}
                                                    />
                                                </View>
                                            </View>
                                        </View>
                                    </ScrollView>
                                </Card>
                                <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                                    <FormActions
                                        confirmText='close'
                                        onSubmit={closeModal}
                                        cancelText={!data.deactivate ? 'deactivateCitizen' : 'activateCitizen'}
                                        cancelType={!data.deactivate ? 'destructive' : 'secondary'}
                                        onClose={async () => {
                                            if (data.deactivate) {
                                                const result = await toggleCitizenActivation(data.id);
                                                if (result) {
                                                    refetch();
                                                }
                                            } else {
                                                setShowDeactivateCitizenModal(true);
                                            }
                                        }}
                                        disabled={false}
                                    />
                                </View>
                            </>}
                    </Card>
                </View>
            </View>
            <DeactivateCitizenModal
                citizenId={data.id}
                isModalVisible={showDeactivateCitizenModal}
                setModalVisible={setShowDeactivateCitizenModal}
                onConfirm={refetch}
            />
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 850,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        rowGap: 32,
        height: isMobile ? '95%' : 669,
    }),
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    fieldsLabelStyleReason: {
        color: theme.colors.destructiveButton.hoverBackground,
        fontWeight: '600',
        fontSize: 14,
    },
    itemView: (flex: number) => ({
        gap: 8,
        flex
    }),
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10
    },
}));
